import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { logoutUser } from "../store/authSlice";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import { apiService } from "../services/api";
import NotificationCenter from "./NotificationCenter";
import "./../style/general.css";
import "@fortawesome/fontawesome-free/css/all.min.css";

interface HeaderProps {
  setProfileSettingsIsOpen: (isOpen: boolean) => void;
  onNavigateToLead?: (leadId: string) => void;
}

interface SubscriptionStatus {
  status: string;
  isActive: boolean;
  currentPeriodEnd: string | null;
  trialEndDate: string | null;
  gracePeriodEndDate: string | null;
  cancelAtPeriodEnd: boolean;
  hasStripeCustomer: boolean;
  hasStripeSubscription: boolean;
  hasPaymentMethod: boolean;
  isInTrial: boolean;
  isInGracePeriod: boolean;
  trialDaysRemaining: number;
  gracePeriodDaysRemaining: number;
}

const Header: React.FC<HeaderProps> = ({
  setProfileSettingsIsOpen,
  onNavigateToLead,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const open = Boolean(anchorEl);
  const [activeButton, setActiveButton] = React.useState<string>("leads");

  useEffect(() => {
    const path = location.pathname.replace("/", "");
    if (path === "leads" || path === "dashboard") {
      setActiveButton(path);
    }
  }, [location]);

  // Remove this useEffect to prevent navigation loop
  // Navigation to /sign_in is now handled explicitly in handleLogout
  // useEffect(() => {
  //   if (!isAuthenticated) {
  //     navigate("/sign_in");
  //   }
  // }, [isAuthenticated, navigate]);

  // Fetch subscription status when component mounts
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      // Don't fetch if user is not authenticated, no user object, or on sign-in page
      if (!isAuthenticated || !user || location.pathname === "/sign_in") {
        return;
      }

      setIsLoadingStatus(true);
      try {
        const status = await apiService.getSubscriptionStatus();
        setSubscriptionStatus(status as any);
      } catch (error: any) {
        // If we get a 401 Unauthorized, clear all storage and redirect to sign-in
        if (error?.response?.status === 401) {
          console.error(
            "Unauthorized access - clearing storage and redirecting to sign-in"
          );
          apiService.clearAllStorage();
          dispatch(logoutUser());
          navigate("/sign_in", { replace: true });
          return;
        }
        console.error("Failed to fetch subscription status:", error);
      } finally {
        setIsLoadingStatus(false);
      }
    };

    fetchSubscriptionStatus();
  }, [isAuthenticated, user, location.pathname, navigate, dispatch]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    try {
      await dispatch(logoutUser()).unwrap();
      navigate("/sign_in"); // Add explicit navigation
    } catch (error) {
      console.error("Logout failed:", error);
      navigate("/sign_in");
    }
  };

  const handleNavButton = (button: string) => {
    navigate(`/${button}`);
  };

  const handleManageSubscription = async () => {
    handleClose();
    setIsLoadingSubscription(true);

    try {
      console.log("🔄 Creating customer portal session...");

      // Create the customer portal session (backend will handle customer creation)
      const returnUrl = `${window.location.origin}/dashboard`; // Return to dashboard after managing subscription
      const portalSession = await apiService.createCustomerPortalSession(
        returnUrl
      );

      console.log("✅ Portal session created:", portalSession);
      console.log("🚀 Redirecting to Stripe portal:", portalSession.url);

      // Redirect to Stripe customer portal
      window.location.href = portalSession.url;
    } catch (error) {
      console.error("❌ Failed to create subscription portal:", error);
      // Handle the error gracefully - maybe show a notification or alert
      alert(
        "Unable to access subscription management. Please try again later."
      );
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  const handlePayInvoice = async () => {
    setIsLoadingSubscription(true);

    try {
      console.log("🔄 Creating subscription...");

      // Try to create a subscription first
      const result = await apiService.createSubscription();

      console.log("✅ Subscription created:", result);

      // Wait a moment for the database to update
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Refresh subscription status
      try {
        const status = await apiService.getSubscriptionStatus();
        setSubscriptionStatus(status as any);

        if (status?.isActive) {
          alert(
            "🎉 Subscription activated successfully! You now have full access to all features."
          );
        } else {
          // If subscription is not immediately active (e.g., requires payment), redirect to portal
          console.log(
            "Subscription created but not active, redirecting to portal..."
          );
          await handleManageSubscription();
        }
      } catch (statusError: any) {
        // If we get a 401 Unauthorized, clear all storage and redirect to sign-in
        if (statusError?.response?.status === 401) {
          console.error(
            "Unauthorized access - clearing storage and redirecting to sign-in"
          );
          apiService.clearAllStorage();
          dispatch(logoutUser());
          navigate("/sign_in", { replace: true });
          return;
        }
        // If subscription status fetch fails, still try to redirect to portal
        console.log(
          "Subscription created but status check failed, redirecting to portal..."
        );
        await handleManageSubscription();
      }
    } catch (error: any) {
      console.error("❌ Failed to create subscription:", error);

      // If subscription creation fails, fall back to customer portal
      if (error?.response?.data?.message?.includes("price configured")) {
        alert(
          "Subscription service not configured. Redirecting to payment management..."
        );
        await handleManageSubscription();
      } else {
        alert(
          "Unable to create subscription. Please try again or contact support."
        );
      }
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  const refreshSubscriptionStatus = async () => {
    // Don't refresh if not authenticated or on sign-in page
    if (!isAuthenticated || !user || location.pathname === "/sign_in") {
      return;
    }

    setIsLoadingStatus(true);
    try {
      const status = await apiService.getSubscriptionStatus();
      setSubscriptionStatus(status as any);
      console.log("🔄 Subscription status refreshed:", status);
    } catch (error: any) {
      // If we get a 401 Unauthorized, clear all storage and redirect to sign-in
      if (error?.response?.status === 401) {
        console.error(
          "Unauthorized access - clearing storage and redirecting to sign-in"
        );
        apiService.clearAllStorage();
        dispatch(logoutUser());
        navigate("/sign_in", { replace: true });
        return;
      }
      console.error("Failed to refresh subscription status:", error);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const getSubscriptionStatusDisplay = () => {
    // Hide subscription status for admin users
    if (user?.role === "ADMIN") {
      return null;
    }

    if (isLoadingStatus) {
      return (
        <div className="flex items-center gap-2 text-sm">
          <i className="fa-solid fa-spinner fa-spin text-gray-500"></i>
          <span className="text-gray-500">Loading...</span>
        </div>
      );
    }

    if (!subscriptionStatus) {
      return (
        <div className="flex items-center gap-2 text-sm">
          <i className="fa-solid fa-exclamation-triangle text-yellow-500"></i>
          <span className="text-gray-600">Status Unknown</span>
        </div>
      );
    }

    if (subscriptionStatus.isInTrial) {
      return (
        <div className="flex items-center gap-2 text-sm">
          <i className="fa-solid fa-clock text-blue-500"></i>
          <span className="text-blue-600 font-medium">
            Trial ({subscriptionStatus.trialDaysRemaining} days left)
          </span>
          <button
            onClick={refreshSubscriptionStatus}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Refresh status"
          >
            <i className="fa-solid fa-refresh text-xs"></i>
          </button>
        </div>
      );
    }

    if (subscriptionStatus.isInGracePeriod) {
      return (
        <div className="flex items-center gap-2 text-sm">
          <i className="fa-solid fa-exclamation-triangle text-orange-500"></i>
          <span className="text-orange-600 font-medium">
            Grace Period ({subscriptionStatus.gracePeriodDaysRemaining} days
            left)
          </span>
          <button
            onClick={refreshSubscriptionStatus}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Refresh status"
          >
            <i className="fa-solid fa-refresh text-xs"></i>
          </button>
        </div>
      );
    }

    if (subscriptionStatus.isActive) {
      return (
        <div className="flex items-center gap-2 text-sm">
          <i className="fa-solid fa-check-circle text-green-500"></i>
          <span className="text-green-600 font-medium">Active</span>
          <button
            onClick={refreshSubscriptionStatus}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Refresh status"
          >
            <i className="fa-solid fa-refresh text-xs"></i>
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 text-sm">
        <i className="fa-solid fa-times-circle text-red-500"></i>
        <span className="text-red-600 font-medium">Inactive</span>
        <button
          onClick={refreshSubscriptionStatus}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title="Refresh status"
        >
          <i className="fa-solid fa-refresh text-xs"></i>
        </button>
      </div>
    );
  };

  const dashboardButtonClass =
    activeButton === "dashboard"
      ? "text-lightestGray bg-darkBlue"
      : "text-darkGray bg-lightestGray";
  const leadsButtonClass =
    activeButton === "leads"
      ? "text-lightestGray bg-darkBlue"
      : "text-darkGray bg-lightestGray";

  return (
    <header className="flex justify-between items-center px-8 py-5 m-2 h-20 bg-lightestGray border border-lighterGray rounded-2xl mh-box-shadow">
      <div className="flex gap-x-1">
        <button
          onClick={() => handleNavButton("leads")}
          className={`flex gap-x-2 items-center px-6 py-2 rounded-3xl hover:text-lighterGray hover:bg-mediumBlue ${leadsButtonClass}`}
        >
          <i className="fa-solid fa-comments"></i>
          <span className="text-h3 font-medium">Leads</span>
        </button>
        <button
          onClick={() => handleNavButton("dashboard")}
          className={`flex gap-x-2 items-center px-6 py-2 rounded-3xl hover:text-lighterGray hover:bg-mediumBlue ${dashboardButtonClass}`}
        >
          <i className="fa-solid fa-chart-pie"></i>
          <span className="text-h3 font-medium">Dashboard</span>
        </button>
      </div>
      <div className="flex items-center gap-4">
        {/* Subscription Status Indicator - Hidden for admin users */}
        {user?.role !== "ADMIN" && (
          <div className="flex items-center gap-3">
            {getSubscriptionStatusDisplay()}
            {subscriptionStatus &&
            !subscriptionStatus.hasStripeSubscription &&
            !subscriptionStatus.hasPaymentMethod ? (
              <button
                onClick={handleManageSubscription}
                disabled={isLoadingSubscription}
                className="flex items-center gap-2 px-3 py-1.5 bg-[#000000] hover:bg-blue-600 text-white text-sm font-medium rounded-lg"
              >
                {isLoadingSubscription && (
                  <i
                    className="fa-solid fa-spinner fa-spin"
                    style={{ fontSize: "12px" }}
                  ></i>
                )}
                <i className="fa-solid fa-credit-card"></i>
                Add Payment Method
              </button>
            ) : (
              subscriptionStatus &&
              !subscriptionStatus.isActive && (
                <button
                  onClick={handlePayInvoice}
                  disabled={isLoadingSubscription}
                  className="flex items-center gap-2 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingSubscription && (
                    <i
                      className="fa-solid fa-spinner fa-spin"
                      style={{ fontSize: "12px" }}
                    ></i>
                  )}
                  <i className="fa-solid fa-credit-card"></i>
                  {subscriptionStatus.isInTrial
                    ? "Subscribe Now"
                    : "Pay Invoice"}
                </button>
              )
            )}
          </div>
        )}

        {/* Notification Center */}
        {onNavigateToLead && (
          <NotificationCenter onNavigateToLead={onNavigateToLead} />
        )}

        <Button
          id="basic-button"
          aria-controls={open ? "basic-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontFamily: "Inter , sans-serif",
            textTransform: "none",
            padding: 0,
            "&:hover": {
              backgroundColor: "transparent",
              boxShadow: "none",
            },
          }}
        >
          <p className="text-h2 font-semibold text-pureBlack hidden lg:block">
            {user?.firstName} {user?.lastName}
          </p>
          <i className="fa-solid fa-angle-down text-darkGray"></i>
        </Button>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
            sx: {
              paddingTop: 0,
              paddingBottom: 0,
            },
          }}
          elevation={0}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            style: {
              width: "200px",
              borderRadius: "8px",
              border: "1px solid #222830",
              background: "#F3F5F7",
              boxShadow: "0 4px 24px #CBD5E066",
              transform: "translateY(10px)",
            },
          }}
        >
          <MenuItem
            onClick={() => {
              handleClose();
              setProfileSettingsIsOpen(true);
            }}
            sx={{
              fontFamily: "Inter , sans-serif",
              fontSize: "16px",
              fontWeight: "600",
              color: "#222830",
              padding: "15px",
            }}
          >
            Profile Settings
          </MenuItem>
          <Divider sx={{ margin: "0 !important", borderColor: "#323C48" }} />
          <MenuItem
            onClick={handleManageSubscription}
            disabled={isLoadingSubscription}
            sx={{
              fontFamily: "Inter , sans-serif",
              fontSize: "16px",
              fontWeight: "600",
              color: "#222830",
              padding: "15px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {isLoadingSubscription && (
              <i
                className="fa-solid fa-spinner fa-spin"
                style={{ fontSize: "14px" }}
              ></i>
            )}
            Manage Subscription
          </MenuItem>
          <Divider sx={{ margin: "0 !important", borderColor: "#323C48" }} />
          <MenuItem
            onClick={handleLogout}
            sx={{
              fontFamily: "Inter , sans-serif",
              fontSize: "16px",
              fontWeight: "600",
              color: "#222830",
              padding: "15px",
            }}
          >
            Log Out
          </MenuItem>
        </Menu>
      </div>
    </header>
  );
};

export default Header;
